import { LANGUAGE_PREF_KEY } from '../config';

/** Map stored/browser codes (e.g. zh, zh-cn) onto API langCodes (e.g. zh-hans). */
export function resolveMatchingLanguageCode(
  code: string | undefined,
  options: string[],
): string | undefined {
  if (!code) {
    return undefined;
  }

  const exact = code.toLowerCase().replace(/_/g, '-');
  if (options.includes(exact)) {
    return exact;
  }

  const normalized = normalizeLanguageCode(code);
  if (options.includes(normalized)) {
    return normalized;
  }

  const short = normalized.slice(0, 2);
  if (options.includes(short)) {
    return short;
  }

  return options.find((option) => option.startsWith(`${short}-`));
}

/** Normalize browser / legacy codes to API-facing language codes. */
export function normalizeLanguageCode(code: string): string {
  const normalized = code.toLowerCase().replace(/_/g, '-');

  if (normalized === 'zh' || normalized.startsWith('zh-')) {
    const traditionalHints = ['zh-tw', 'zh-hk', 'zh-mo', 'zh-hant'];
    const isTraditional = traditionalHints.some((hint) => (
      normalized === hint || normalized.startsWith(`${hint}-`)
    )) || normalized.includes('hant');
    return isTraditional ? 'zh-hant' : 'zh-hans';
  }

  if (normalized === 'pt-br' || normalized.startsWith('pt-br')) {
    return 'pt-br';
  }

  if (normalized.includes('-')) {
    return normalized.slice(0, 2);
  }

  return normalized;
}

/** Language family for suggestion matching (zh / zh-hans / zh-hant → zh). */
export function getLanguageFamily(code?: string) {
  if (!code) {
    return undefined;
  }

  const normalized = code.toLowerCase().replace(/_/g, '-');
  if (normalized === 'pt-br' || normalized.startsWith('pt-br')) {
    return 'pt-br';
  }

  return normalized.slice(0, 2);
}

export function isSameLanguageFamily(a?: string, b?: string) {
  const familyA = getLanguageFamily(a);
  const familyB = getLanguageFamily(b);

  return Boolean(familyA && familyB && familyA === familyB);
}

export function getPersistedLanguage(): string | undefined {
  try {
    return localStorage.getItem(LANGUAGE_PREF_KEY) || undefined;
  } catch (e) {
    return undefined;
  }
}

export function persistLanguage(langCode: string) {
  try {
    localStorage.setItem(LANGUAGE_PREF_KEY, langCode);
  } catch (e) {
    // Ignore quota / private mode errors
  }
}

/**
 * Resolve UI language for app start.
 * Prefers an explicitly persisted choice; legacy default `zh` is treated as unset
 * and falls back to the system language.
 */
export function resolveLanguagePreference(current?: string): string {
  const persisted = getPersistedLanguage();
  const candidate = persisted || current;

  // Bare `zh` is the legacy default and is not present in the API language list.
  if (candidate && candidate !== 'zh') {
    return normalizeLanguageCode(candidate);
  }

  const navLang = (typeof navigator !== 'undefined' && navigator.language) || 'zh-CN';
  return normalizeLanguageCode(navLang);
}
